/* src/services/api.js */
const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const apiBaseUrl = process.env.NODE_ENV === 'development'
  ? baseUrl
  : `${baseUrl}/api`;
const isApiDebugEnabled = process.env.REACT_APP_API_DEBUG === '1';

export const apiService = {
  async fetch(endpoint, options = {}) {
    // S'assurer que l'endpoint commence par "/"
    const finalEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const token = localStorage.getItem('auth_token');
    const defaultHeaders = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    const requestHeaders = {
      ...defaultHeaders,
      ...options.headers,
    };

    if (isApiDebugEnabled) {
      console.log(
        `[apiService.fetch] Endpoint: ${apiBaseUrl}${finalEndpoint}, Method: ${options.method || 'GET'}, Headers:`,
        JSON.stringify(requestHeaders)
      );
    }


    const response = await fetch(`${apiBaseUrl}${finalEndpoint}`, {
      ...options,
      headers: requestHeaders,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expirÃ© ou invalide
        localStorage.removeItem('auth_token');
        // Optionnel: rediriger vers la page de connexion
        window.location.href = '/login';
        throw new Error('Session expirÃ©e. Veuillez vous reconnecter.');
      }
      // Si la rÃ©ponse n'est pas ok, on essaye de rÃ©cupÃ©rer l'erreur en JSON
      const error = await response.json().catch(() => ({}));
      console.error(`[apiService.fetch] Error for ${finalEndpoint}:`, error);
      let message = 'Une erreur est survenue';
      if (error && error.detail) {
        if (Array.isArray(error.detail)) {
          // FastAPI validation errors array
          const msgs = error.detail.map((d) => (typeof d?.msg === 'string' ? d.msg : JSON.stringify(d))).join('; ');
          message = msgs || message;
        } else if (typeof error.detail === 'string') {
          message = error.detail;
        } else {
          message = JSON.stringify(error.detail);
        }
      }
      throw new Error(message);
    }

    return response.status !== 204 ? response.json() : null;
  },

  get(endpoint) {
    return this.fetch(endpoint);
  },

  post(endpoint, data) {
    return this.fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  put(endpoint, data) {
    return this.fetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  patch(endpoint, data) {
    return this.fetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  delete(endpoint) {
    return this.fetch(endpoint, {
      method: 'DELETE'
    });
  },

  createTask(taskData) {
    return this.post('/tasks', taskData);
  },

  createTaskForObjective(objectiveId, taskData) {
    return this.post(`/objectives/${objectiveId}/tasks`, taskData);
  },

  updateTask(taskId, updateData) {
    return this.patch(`/tasks/${taskId}`, updateData).then((response) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('nexus-proactivity-refresh', {
            detail: { source: 'task-update', taskId },
          })
        );
      }
      return response;
    });
  },

  deleteTask(taskId) {
    return this.delete(`/tasks/${taskId}`);
  },

  getMyTasks() {
    return this.get('/my-tasks');
  },

  getNotifications() {
    return this.get('/notifications');
  },

  markAllNotificationsRead() {
    return this.post('/notifications/mark-all-read', {});
  },

  markNotificationRead(notificationId) {
    return this.post(`/notifications/${notificationId}/read`, {});
  },

  deleteNotification(notificationId) {
    return this.delete(`/notifications/${notificationId}`);
  },

  deleteAllNotifications() {
    return this.delete('/notifications');
  },

  getNexusProactivityRequests() {
    return this.get('/nexus/proactivity/requests');
  },

  respondToNexusProactivityRequest(requestId, decision) {
    return this.post(`/nexus/proactivity/requests/${requestId}`, { decision });
  },

  // --- Automatisations Nexus ---
  getAutomations() {
    return this.get('/nexus/automations');
  },

  createAutomation(payload) {
    return this.post('/nexus/automations', payload);
  },

  updateAutomation(automationId, payload) {
    return this.put(`/nexus/automations/${automationId}`, payload);
  },

  deleteAutomation(automationId) {
    return this.delete(`/nexus/automations/${automationId}`);
  },

  toggleAutomation(automationId, isActive) {
    return this.post(`/nexus/automations/${automationId}/activate`, { is_active: isActive });
  },

  runAutomation(automationId) {
    return this.post(`/nexus/automations/${automationId}/run`, {});
  },

  getAutomationRuns(automationId) {
    return this.get(`/nexus/automations/${automationId}/runs`);
  },

  getAutomationMessages() {
    return this.get('/nexus/automations/messages');
  },

  acknowledgeAutomationMessage(messageId) {
    return this.post(`/nexus/automations/messages/${messageId}/ack`, {});
  },

  // Nouvelles mÃ©thodes pour le support du streaming

  // RÃ©cupÃ©rer l'URL de base de l'API
  getBaseUrl() {
    return apiBaseUrl;
  },

  // RÃ©cupÃ©rer les en-tÃªtes d'authentification
  getHeaders() {
    const token = localStorage.getItem('auth_token');
    if (token) {
      return {
        'Authorization': `Bearer ${token}`
      };
    }
    return {};
  },
  // Nouvelle fonction pour la suggestion de titre
  async suggestObjectiveTitle(projectId, name) {
    const endpoint = `/projects/${projectId}/objectives/suggest-title?name=${encodeURIComponent(name)}`;
    return this.get(endpoint);
  },

  getProjectOptions(options = {}) {
    const params = new URLSearchParams();
    if (options.statuses) params.set('statuses', Array.isArray(options.statuses) ? options.statuses.join(',') : options.statuses);
    if (options.includeChildren !== undefined) params.set('include_children', String(options.includeChildren));
    if (options.circleId) params.set('circle_id', String(options.circleId));
    const query = params.toString();
    return this.get(`/projects/options${query ? `?${query}` : ''}`);
  },

  getKnowledgeProjectStatus(projectId) {
    return this.get(`/knowledge/projects/${projectId}/status`);
  },

  getKnowledgeProjectStatuses(projectIds = []) {
    const ids = Array.isArray(projectIds) ? projectIds.filter(Boolean).join(',') : String(projectIds || '');
    return this.get(`/knowledge/projects/statuses${ids ? `?project_ids=${encodeURIComponent(ids)}` : ''}`);
  },

  indexKnowledgeProject(projectId, payload = {}) {
    return this.post(`/knowledge/projects/${projectId}/index`, payload);
  },

  syncKnowledgeProjectDrive(projectId) {
    return this.post(`/knowledge/projects/${projectId}/drive-sync`, {});
  },

  getKnowledgeJob(jobId) {
    return this.get(`/knowledge/jobs/${jobId}`);
  },

  searchKnowledge(payload) {
    return this.post('/knowledge/search', payload);
  },

  getKnowledgeSource(sourceId) {
    return this.get(`/knowledge/sources/${sourceId}`);
  },

  subscribeToObjective(projectId, objectiveId) {
    return this.post(`/projects/${projectId}/objectives/${objectiveId}/subscriptions`, {});
  },

  unsubscribeFromObjective(projectId, objectiveId) {
    return this.delete(`/projects/${projectId}/objectives/${objectiveId}/subscriptions`);
  },

  async suggestJournalTitle(projectId, content) {
    const endpoint = `/projects/${projectId}/journal/suggest-title`;
    return this.post(endpoint, { content });
  },

  getGoogleDriveStatus() {
    return this.get('/auth/google/drive/status');
  },

  startGoogleDriveAuth(returnTo) {
    return this.post('/auth/google/drive/start', { return_to: returnTo });
  },

  disconnectGoogleDrive() {
    return this.delete('/auth/google/drive');
  },

  getProjectDriveFiles(projectId, options = {}) {
    const params = new URLSearchParams();
    if (options.folderId) params.set('folder_id', options.folderId);
    if (options.pageToken) params.set('page_token', options.pageToken);
    if (options.pageSize) params.set('page_size', String(options.pageSize));
    const query = params.toString();
    return this.get(`/projects/${projectId}/drive/files${query ? `?${query}` : ''}`);
  },

  searchProjectDriveFiles(projectId, options = {}) {
    const params = new URLSearchParams();
    params.set('q', options.query || '');
    params.set('recursive', String(options.recursive ?? true));
    params.set('search_content', String(options.searchContent ?? false));
    if (options.maxResults) params.set('max_results', String(options.maxResults));
    return this.fetch(`/projects/${projectId}/drive/search?${params.toString()}`, {
      signal: options.signal,
    });
  },

  searchStockSerials(query, limit = 10) {
    const params = new URLSearchParams({
      query,
      limit: String(limit),
    });
    return this.get(`/inventory/stock/search?${params.toString()}`);
  },

  async suggestCircleTensionTitle(changes) {
    const endpoint = `/circle-tensions/suggest-title`;
    return this.post(endpoint, { changes });
  },

  // --- PrÃ©fÃ©rences IA utilisateur ---
  getUserInstructions() {
    return this.get('/users/me/instructions');
  },

  addUserInstruction(payload) {
    return this.post('/users/me/instructions', payload);
  },

  updateUserInstruction(instructionId, payload) {
    return this.put(`/users/me/instructions/${instructionId}`, payload);
  },

  deleteUserInstruction(instructionId) {
    return this.delete(`/users/me/instructions/${instructionId}`);
  },

  getUserKnowledge() {
    return this.get('/users/me/knowledge');
  },

  addUserKnowledge(payload) {
    return this.post('/users/me/knowledge', payload);
  },

  updateUserKnowledge(knowledgeId, payload) {
    return this.put(`/users/me/knowledge/${knowledgeId}`, payload);
  },

  deleteUserKnowledge(knowledgeId) {
    return this.delete(`/users/me/knowledge/${knowledgeId}`);
  },

  // --- Tableau des attributs projet ---
  getProjectAttributes(projectId) {
    return this.get(`/projects/${projectId}/attributes`);
  },

  createProjectAttribute(projectId, payload) {
    return this.post(`/projects/${projectId}/attributes`, payload);
  },

  updateProjectAttribute(projectId, attributeId, payload) {
    return this.put(`/projects/${projectId}/attributes/${attributeId}`, payload);
  },

  updateProjectAttributeStatus(projectId, attributeId, payload) {
    return this.patch(`/projects/${projectId}/attributes/${attributeId}/status`, payload);
  },

  deleteProjectAttribute(projectId, attributeId) {
    return this.delete(`/projects/${projectId}/attributes/${attributeId}`);
  },

  getProjectAttributeHistory(projectId, limit = 150) {
    return this.get(`/projects/${projectId}/attributes/history?limit=${limit}`);
  },

  updateProjectAttributeHistoryEntry(projectId, attributeId, historyId, payload) {
    return this.put(`/projects/${projectId}/attributes/${attributeId}/history/${historyId}`, payload);
  },

  deleteProjectAttributeHistoryEntry(projectId, attributeId, historyId) {
    return this.delete(`/projects/${projectId}/attributes/${attributeId}/history/${historyId}`);
  },

  getProjectAttributeTargets(projectId) {
    return this.get(`/projects/${projectId}/attribute-targets`);
  },

  getIterationAttributeTargets(iterationId) {
    return this.get(`/iterations/${iterationId}/attribute-targets`);
  },

  createIterationAttributeTarget(iterationId, payload) {
    return this.post(`/iterations/${iterationId}/attribute-targets`, payload);
  },

  updateIterationAttributeTarget(iterationId, targetId, payload) {
    return this.put(`/iterations/${iterationId}/attribute-targets/${targetId}`, payload);
  },

  deleteIterationAttributeTarget(iterationId, targetId) {
    return this.delete(`/iterations/${iterationId}/attribute-targets/${targetId}`);
  },

  getClientContacts(clientId) {
    return this.get(`/clients/${clientId}/contacts`);
  },

  createClientContact(clientId, payload) {
    return this.post(`/clients/${clientId}/contacts`, payload);
  },

  updateClientContact(clientId, contactId, payload) {
    return this.patch(`/clients/${clientId}/contacts/${contactId}`, payload);
  },

  deleteClientContact(clientId, contactId) {
    return this.delete(`/clients/${clientId}/contacts/${contactId}`);
  },

  getProjectClientContacts(projectId) {
    return this.get(`/projects/${projectId}/client-contacts`);
  },

  addProjectClientContact(projectId, payload) {
    return this.post(`/projects/${projectId}/client-contacts`, payload);
  },

  updateProjectClientContact(projectId, contactId, payload) {
    return this.patch(`/projects/${projectId}/client-contacts/${contactId}`, payload);
  },

  removeProjectClientContact(projectId, contactId) {
    return this.delete(`/projects/${projectId}/client-contacts/${contactId}`);
  },

  // Methode pour upload de fichiers (formulaire multipart)
  async uploadFile(endpoint, formData, onProgress = null, method = 'POST') {
    const token = localStorage.getItem('auth_token');
    const finalEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    console.log(`[apiService.uploadFile] Endpoint: ${finalEndpoint}, Method recue: ${method}`);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      xhr.open(method, `${apiBaseUrl}${finalEndpoint}`);
      console.log(`[apiService.uploadFile] XHR ouverte avec methode: ${method}`);

      const requestHeadersLog = {};
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        requestHeadersLog['Authorization'] = `Bearer ${token ? `${token.substring(0, 10)}...` : 'null'}`;
      }

      console.log(
        `[apiService.uploadFile] Sending request to ${finalEndpoint} with headers:`,
        JSON.stringify(requestHeadersLog)
      );

      xhr.onload = function () {
        console.log(
          `[apiService.uploadFile] Reponse recue - Status: ${xhr.status}, Endpoint: ${finalEndpoint}, Methode XHR: ${method}`
        );
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          if (xhr.status === 401) {
            localStorage.removeItem('auth_token');
            window.location.href = '/login';
            reject(new Error('Session expiree. Veuillez vous reconnecter.'));
            return;
          }
          try {
            const errorJson = JSON.parse(xhr.responseText);
            console.error('[apiService.uploadFile] Erreur JSON:', errorJson);
            reject(new Error(errorJson.detail || `Erreur serveur ${xhr.status}`));
          } catch (e) {
            console.error('[apiService.uploadFile] Erreur parsing reponse erreur:', xhr.responseText);
            reject(new Error(`Erreur serveur ${xhr.status}: ${xhr.responseText.substring(0, 100)}`));
          }
        }
      };

      xhr.onerror = function () {
        console.error('[apiService.uploadFile] Erreur reseau pour', finalEndpoint);
        reject(new Error('Erreur reseau'));
      };

      xhr.send(formData);
    });
  }

};
